import { cn } from "@/utils/cn";
import { renderSwitch } from "@/utils/react-conditional";
import React from "react";
import { ICONS_COMMON } from "../icons/icons-common";
import { EBadgeType } from "./badge-helpers";
import subscriberHeart from "./subscriber-heart.svg";

const BADGE_BASE_CLASS =
  "inline-block whitespace-nowrap rounded-[24px] bg-surface px-1.5 py-0.25 text-center text-small leading-[14px] font-medium text-title-color not-italic";

const BADGE_TYPE_CLASSES: Record<EBadgeType, string> = {
  [EBadgeType.New]:
    "rounded-r-none bg-pink px-1.5 py-0.5 text-small leading-[19px] font-semibold text-text-inverse tablet:px-2 tablet:py-1.25 tablet:text-body",
  [EBadgeType.Free]:
    "rounded-r-none bg-pink px-1.5 py-0.5 text-small leading-[19px] font-semibold text-text-inverse tablet:px-2 tablet:py-1.25 tablet:text-body",
  [EBadgeType.Vip]: "bg-vip-bg text-vip-text",
  [EBadgeType.Staff]: "bg-badge-dark text-text-inverse",
  [EBadgeType.NoCredits]: "bg-badge-muted-alt text-text-inverse",
  [EBadgeType.PaidFan]:
    "border border-success bg-surface px-1.25 py-0 text-success",
  [EBadgeType.Fan]: "bg-badge-fan text-text-inverse",
  [EBadgeType.Affiliate]: "bg-badge-fan text-text-inverse",
  [EBadgeType.Bonus]: "bg-badge-bonus text-text-inverse",
  [EBadgeType.Subscriber]: "bg-badge-subscriber text-text-inverse",
  [EBadgeType.Subscribed]: "bg-badge-subscriber text-text-inverse",
  [EBadgeType.PaidChat]: "flex bg-transparent p-0 text-paid-chat",
  [EBadgeType.FreeTrialChat]: "flex bg-transparent p-0 text-free-trial-chat",
};

const RELATION_BADGE_BASE_CLASS =
  "inline-flex items-center justify-center gap-1 rounded-[6px] border border-transparent px-1.5 py-0 font-semibold";

const RELATION_BADGE_TYPE_CLASSES: Partial<Record<EBadgeType, string>> = {
  [EBadgeType.Vip]: "border-transparent",
  [EBadgeType.Staff]: "border-transparent",
  [EBadgeType.NoCredits]: "border-transparent",
  [EBadgeType.PaidFan]: "border-success",
  [EBadgeType.Fan]: "border-transparent",
  [EBadgeType.Affiliate]: "border-transparent",
  [EBadgeType.Bonus]: "border-transparent",
  [EBadgeType.Subscriber]: "border-transparent",
  [EBadgeType.Subscribed]: "border-transparent",
};

function isRelationBadge(type?: EBadgeType) {
  return !!type && type in RELATION_BADGE_TYPE_CLASSES;
}

function badgeClassName(type?: EBadgeType) {
  return cn(
    BADGE_BASE_CLASS,
    type && BADGE_TYPE_CLASSES[type],
    isRelationBadge(type) && RELATION_BADGE_BASE_CLASS,
    type && RELATION_BADGE_TYPE_CLASSES[type]
  );
}

export const Badge = React.memo(function Badge(props: { type?: EBadgeType }) {
  return (
    <>
      {renderSwitch(
        props.type!,
        {
          // DISCOVER
          [EBadgeType.New]: () => (
            <span className={badgeClassName(props.type)}>NEW</span>
          ),
          [EBadgeType.Free]: () => (
            <span className={badgeClassName(props.type)}>FREE CHAT</span>
          ),
          // PROFILE
          [EBadgeType.Vip]: () => (
            <span className={badgeClassName(props.type)}>VIP</span>
          ),
          [EBadgeType.Staff]: () => (
            <span className={badgeClassName(props.type)}>Staff</span>
          ),
          [EBadgeType.NoCredits]: () => (
            <span className={badgeClassName(props.type)}>No Credits</span>
          ),
          [EBadgeType.PaidFan]: () => (
            <span className={badgeClassName(props.type)}>Paid</span>
          ),
          [EBadgeType.Fan]: () => (
            <span className={badgeClassName(props.type)}>Your Fan</span>
          ),
          [EBadgeType.Affiliate]: () => (
            <span className={badgeClassName(props.type)}>Affiliate</span>
          ),
          [EBadgeType.Bonus]: () => (
            <span className={badgeClassName(props.type)}>Bonus Chat</span>
          ),
          [EBadgeType.Subscriber]: () => (
            <span className={badgeClassName(props.type)}>
              Subscriber
              <img
                src={subscriberHeart}
                alt=""
                className="h-2 w-[9px] shrink-0"
              />
            </span>
          ),
          [EBadgeType.Subscribed]: () => (
            <span className={badgeClassName(props.type)}>
              Subscribed
              <img
                src={subscriberHeart}
                alt=""
                className="h-2 w-[9px] shrink-0"
              />
            </span>
          ),
          [EBadgeType.PaidChat]: () => (
            <span className={badgeClassName(props.type)}>
              {ICONS_COMMON.PaidChatBadge}
            </span>
          ),
          [EBadgeType.FreeTrialChat]: () => (
            <span className={badgeClassName(props.type)}>
              {ICONS_COMMON.PaidChatBadge}
            </span>
          ),
        },
        () => null
      )}
    </>
  );
});
